import { Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GridActionTypeEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { DatagridVisualisationEnum } from '@app/core/enums/datagrid-visualisation.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AssetGroup } from '@app/core/model/entities/asset-group/asset-group';
import { Asset } from '@app/core/model/entities/asset/asset';
import { Entity } from '@app/core/model/entities/entity';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { DATAGRID, VisualisationKey } from '@app/shared/components/datagrid/abstract-datagrid';
import { ServerSideDatagrid } from '@app/shared/components/datagrid/server-side-datagrid';
import { nEq, textContains } from '@app/shared/extra/filter-model-builder';
import { ColumnBuilder } from '@app/shared/grid/column-builder';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { ExportUtils } from '@app/shared/grid/export-utils';
import { GridOptionsService } from '@app/shared/grid/grid-options.service';
import { GridConfigService } from '@services/grid-config.service';
import { GridReadyEvent } from 'ag-grid-community';
import { MultiRowSelectionOptions } from 'ag-grid-community/dist/types/core/entities/gridOptions';
import { Observable, of } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  templateUrl: './add-existing-assets-modal.component.html',
  providers: [
    ColumnBuilder,
    GridOptionsService,
    GridConfigService,
    ExportUtils,
    {provide: DATAGRID, useValue: null}
  ],
  standalone: false
})
export class AddExistingAssetsModalComponent extends ServerSideDatagrid implements OnInit {
  private readonly assetGroup = inject<{ assetGroup: AssetGroup }>(MAT_DIALOG_DATA).assetGroup;
  private readonly assetsService = inject(AssetsService);
  protected datagrid = <DatagridVisualisationEnum>inject(DATAGRID);
  public nameSearchControl: FormControl<string> = new FormControl(null);

  constructor() {
    super();
    this.initGridOptions({
      rowSelection: <MultiRowSelectionOptions>{
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true,
        enableSelectionWithoutKeys: true
      },
      localeText: this.columnBuilder.localeTextCommon(),
      suppressDragLeaveHidesColumns: true,
      suppressContextMenu: true,
      domLayout: 'autoHeight'
    });
  }

  /**
   * Subscribe to value changes from the name search input to trigger server side data reloading.
   */
  public ngOnInit(): void {
    super.ngOnInit();

    this.nameSearchControl.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.gridApi.refreshServerSide({purge: true});
      });
  }

  /**
   * Save the grid API and initialise the server-side datasource once the grid is ready.
   * @param params Ag-grid params.
   */
  public onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);

    this.gridConfigService.getGridConfig('select_asset_grid')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({columnDefs}) => {
        const colDefs = columnDefs.map(colDef => ({
          ...colDef,
          filter: false,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true
        }));
        this.gridApi.setGridOption('columnDefs', colDefs);
      });

    this.initServerSideDatasource();
  }

  /**
   * Filters that have to be applied by default, excluding Assets already in the current Asset Group.
   * @return A list of default filters.
   * @protected
   */
  public override get defaultFilters(): Filter[] {
    const defaultFilters = [nEq('assetGroupIds', this.assetGroup.id)];
    if (this.nameSearchControl.value) {
      defaultFilters.push(textContains('name', this.nameSearchControl.value));
    }
    return defaultFilters;
  }

  /**
   * Retrieves grid Assets from the API based on the given SelectParams.
   * @param selectParams The parameters defining pagination, filters, sorting, and grouping.
   * @return An Observable emitting an array of Assets corresponding to the grid data.
   * @protected
   */
  protected getGridEntities(selectParams: SelectParams): Observable<Entity[]> {
    return this.assetsService.loadAssetsPage(selectParams);
  }

  /**
   * Retrieves all Assets from the API based on the given SelectParams.
   * @param selectParams The parameters defining pagination, filters, sorting, and grouping.
   * @return An Observable emitting an array of Assets corresponding to the grid data.
   * @protected
   */
  protected loadAllEntities(selectParams: SelectParams): Observable<Entity[]> {
    return this.assetsService.loadAllAssets(selectParams);
  }

  /**
   * This simple picker grid does not support column filters.
   * @protected
   */
  protected getSetFiltersValues(): Observable<Entity[]> {
    return of([]);
  }

  /**
   * This simple picker grid does not persist a visualisation preference.
   * @protected
   */
  protected override get preferredVisualisationIdKey(): VisualisationKey | null {
    return null;
  }

  /**
   * @return The Assets currently selected in the grid.
   */
  public get selectedAssets(): Asset[] {
    return this.gridApi?.getSelectedRows() ?? [];
  }

  /**
   * Track navigation event after the User clicks on the search bar.
   */
  public onClickSearchBox(): void {
    this.analyticsService.trackGridActionEvent(GridActionTypeEnum.SEARCH, EntityTypeEnum.ASSET);
  }
}
